import { ColorsConfig } from '@ddt-web/config';
import { CellData, TabData } from '@ddt-web/data-service';
import { ObjectUtil } from '@ddt-web/generic-utils';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as isNumber from 'lodash/isNumber';
import * as merge from 'lodash/merge';
import * as range from 'lodash/range';
import * as set from 'lodash/set';

import { ChartFactory } from '../factories';
import { TooltipDispatcher } from '../tooltips/dispatcher.tooltip';
import { DefaultChartConfig } from './default-chart.config';
import { BarChartName } from './enums/bar-chart-name.enum';
import { ChartType } from './enums/chart-type.enum';

declare const Highcharts;

const DEFAULT_BAR_CHART_CONFIG: any = {
	legend: {
		enabled: true,
		floating: true,
		verticalAlign: 'bottom',
		itemMarginTop: 2,
		itemStyle: {
			fontFamily: '"FortBold", Arial, sans-serif',
			fontSize: '14px',
			lineHeight: '20px'
		},
		y: -2,
		align: 'left'
	},
	plotOptions: {
		series: {
			pointPadding: 0.15
		}
	},
	tooltip: {
		formatter: function (): string {
			return '<b>' + this.point.series.name + '</b>' + Highcharts.numberFormat(this.point.y, 2, '.', ',') + '%';
		}
	},
	xAxis: {
		labels: {
			enabled: false
		},
		plotLines: [
			{
				color: '#a5a5a5',
				width: 1,
				value: -0.51
			}
		],
		tickLength: 0,
		title: {
			enabled: false
		}
	},
	yAxis: {
		gridLineWidth: 0,
		labels: {
			enabled: false
		},
		title: {
			enabled: false
		}
	}
};

const BENCHMARK_CHART_CONFIG: any = {
	legend: {
		enabled: true,
		floating: true,
		verticalAlign: 'bottom',
		itemMarginTop: 2,
		itemStyle: {
			fontFamily: '"FortBold", Arial, sans-serif',
			fontSize: '14px',
			lineHeight: '20px'
		},
		align: 'left',
		margin: 5,
		layout: 'vertical'
	},
	xAxis: {
		plotLines: [
			{
				color: '#a5a5a5',
				width: 1,
				value: -0.51
			}
		],
		lineColor: 'transparent'
	},
	yAxis: {
		gridLineWidth: 1,
		gridLineColor: '#a5a5a5',
		gridLineDashStyle: 'Dash'
	},
	title: { enabled: false },
	chart: {
		marginBottom: 30
	}
};

export class BarChartConfig extends DefaultChartConfig {
	private seriesColors: Array<string> = [];
	private seriesData: Array<any> = [];
	private seriesCategories: Array<any> = [];

	/**
	 * setTickPositionsForChart is used to set the tick positions and xAxis categories based on the maximum absolute value
	 *
	 * @param {TabData} tabData
	 * @param {Object} highchartCustomAttributes
	 */
	public static setTickPositionsForChart(tabData: TabData, highchartCustomAttributes, isReportMode: boolean = false): void {
		const values: number[] = flatten(get(tabData, 'tableData[0].cells')).map((cell: CellData) => Math.abs(get(cell, 'rawValue', 0)));
		const tickValue = 0.25;
		const roundValue = 1 / tickValue;
		const upperLimit = Math.ceil(Math.max(...values) * roundValue) / roundValue;

		let tickPositions = [];

		const numberOfTicks = upperLimit / tickValue;
		const tickAmount = upperLimit / numberOfTicks;
		tickPositions = range(numberOfTicks * 2 + 1).map(index => (index - numberOfTicks) * tickAmount);

		if (isReportMode) {
			// for pdf, keep the amount of ticks to 5 due to space if we have more than 5 ticks
			tickPositions = tickPositions.length <= 5 ? tickPositions : [-upperLimit, -upperLimit / 2, 0, upperLimit / 2, upperLimit];
		}

		const categories = tickPositions.map(tick => (tick * 100).toFixed(1));
		set(highchartCustomAttributes, 'yAxis.tickPositions', tickPositions);
		set(highchartCustomAttributes, 'xAxis.categories', categories);
	}

	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: any,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: BarChartCustomAttributes,
		protected tooltipType?: string
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_BAR_CHART_CONFIG);
		this.configuration.tooltip.formatter = new TooltipDispatcher(this.type, this.tooltipType).getTooltipFormatter();

		if (this.data instanceof TabData) {
			if (this.name === BarChartName.StackedBarChart) {
				this.buildSeriesDataForStackedChart();
			} else {
				this.seriesColors = this.configuration.colors || ChartFactory.getPortfolioColors();
				this.seriesData = ChartFactory.getDefaultBarSeriesForTabData(this.data,
					get(this.customAttributes, 'portfolioNames'), get(this.customAttributes, 'multiplier', 1), this.customAttributes
				);
				this.seriesCategories = ChartFactory.getCategoriesForTabData(this.data);
			}
		}
		if (this.data instanceof Array) {
			this.seriesColors = this.configuration.colors || ChartFactory.getPortfolioColors();
			this.seriesData = ChartFactory.getBarSeriesForCombinedTabData(this.data, this.customAttributes.portfolioNames, get(this.customAttributes, 'multiplier', 1));
			this.seriesCategories = ChartFactory.getCategoriesForTabData(this.data[0]);
		}

		if (this.name === 'center-align-chart' || this.name === 'chart-with-tick-positions') {
			const indexOfBenchmarkPortfolio = get(this.customAttributes, 'indexOfBenchmarkPortfolio');
			// to maintain the same color mapping of legends in both the portfolio and vs Benchmark view
			if (isNumber(indexOfBenchmarkPortfolio) && indexOfBenchmarkPortfolio >= 0) {
				const chartColors = [...ChartFactory.getPortfolioColors()];
				chartColors.splice(indexOfBenchmarkPortfolio, 1);
				this.seriesColors = chartColors;
			}
			this.updateBenchmarkConfiguration();
		} else {
			this.updateDefaultConfiguration();
		}
	}

	private buildSeriesDataForStackedChart(): void {
		this.seriesColors = this.customAttributes?.colors || ColorsConfig.MEGATRENDS_PORTFOLIO_COLORS;
		this.seriesData = ChartFactory.getStackedSeriesForTabData(this.data,
			get(this.customAttributes, 'portfolioNames'), get(this.customAttributes, 'multiplier', 1)
		);
		this.seriesCategories = ChartFactory.getCategoriesForTabData(this.data);
	};

	private updateDefaultConfiguration(): void {
		let marginTop: number;
		let spacingBottom: number = 15;
		const sibling: any =
			this.container && this.container.parentElement && (this.container.parentElement.previousElementSibling || this.container.parentElement.nextElementSibling);
		// TODO Waris: change selector as tag name can be changed often
		if (sibling && sibling.tagName.toLowerCase() === 'dl-display-table') {
			marginTop = sibling.querySelector('header').offsetHeight - 1;
			spacingBottom = -8;

			// required as stacked bar charts have 2 legends per portfolio -> margin needs to be increased
			if (this.name === BarChartName.StackedBarChart) {
				marginTop += 38;
			}
		}
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: marginTop,
				marginBottom: spacingBottom
			},
			colors: this.seriesColors,
			legend: {
				y: -this.height - 8 + marginTop
			},
			series: this.seriesData,
			xAxis: {
				categories: this.seriesCategories
			}
		});

		if (this.customAttributes) {
			this.configuration = ObjectUtil.extend(this.configuration, this.customAttributes);
		}

		if (this.name === BarChartName.StackedBarChart) {
			// customization for stacked chart
			this.configuration = ObjectUtil.extend(this.configuration, {
				plotOptions: {
					bar: {
						stacking: 'normal'
					}
				},
				yAxis: {
					reversedStacks: false // by default, this is true
				}
			});
		}

		if (get(this.customAttributes, 'contributionTooltipTabData')) {
			ObjectUtil.extend(this.configuration, {
				tooltip: {
					headerFormat: null,
					pointFormat: '{point.tooltip}'
				}
			});
		}
	}

	/**
	 * updates bar chart configuration for all the charts with tick positions
	 */
	public updateBenchmarkConfiguration(): void {
		const chartType: string = get(this.customAttributes, 'type', 'component');
		const labelSize: number = get(this.customAttributes, 'floatingLabelSize') ? this.customAttributes.floatingLabelSize : 14;
		const benchmarkDataIndexToHide = get(this.customAttributes, 'benchmarkDataIndexToHide', -1);
		let marginTop: number = labelSize < 10 ? 10 : 30;
		const marginBottom: number = labelSize < 10 ? 32 : 45;
		const labelPositionOffset: number = labelSize < 10 ? 30 : 50;
		const labelPositionX = (this.configuration.chart.width * 0.5) / 2 - labelPositionOffset;
		const labelPositionY: number = labelSize < 10 ? marginTop : marginTop * 0.7;
		const indexLineHeight: number = labelSize < 10 ? (chartType === 'insight' ? 20 : 24) : 30;
		const spacingTop: number = labelSize < 10 ? 18 : 10;
		const defaultSpacingBetweenGroup: number = labelSize < 10 ? 62 : 52;
		const marginTopOffSetForInsight: number = chartType === 'insight' ? (labelSize < 10 ? 0 : 19) : 0;
		const labelPositionYOffSetForInsight: number = chartType === 'insight' ? (labelSize < 10 ? 0 : -10) : 0;
		let tickPositions = get(this.customAttributes, 'tickPositions', [0, 50, 100]);
		if (get(this.customAttributes, 'autoTickPositions', false)) {
			const maxData = Math.max(...flatten(this.seriesData.map(category => category.data)).map(data => Math.abs(data.y)));
			const upperLimit = Math.ceil(maxData);
			const numberOfTicks = get(this.customAttributes, 'numberOfTicks', 3);
			// The tick amount dynamically varies according to the upperLimit.The tick amount can be either 1,2 or a multiple of 5
			const tickAmount = Math.ceil(upperLimit / numberOfTicks) < 3 ? Math.ceil(upperLimit / numberOfTicks) : Math.ceil((upperLimit / numberOfTicks / 5)) * 5;
			tickPositions = range(numberOfTicks * 2 + 1).map(index => (index - numberOfTicks) * tickAmount);
		}
		// Specifies the marginTop and aligns the chart with to dl-display-table
		const sibling: any =
			this.container && this.container.parentElement && (this.container.parentElement.previousElementSibling || this.container.parentElement.nextElementSibling);
		if (sibling && sibling.tagName.toLowerCase() === 'dl-display-table') {
			marginTop = sibling.querySelector('header').offsetHeight - 2.7;
		}

		if (benchmarkDataIndexToHide > -1) {
			this.seriesData[benchmarkDataIndexToHide].visible = false;
		}

		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: marginTop,
				marginBottom: marginBottom,
				marginLeft: 2,
				events: {
					load: (self, arg) => {
						const ren = self.target.renderer;
						const spacingBetweenGroup = get(self, 'target.axes[0].transA', defaultSpacingBetweenGroup);
						ren.text(this.customAttributes ? this.customAttributes.lowerLabel : 'Lower Exposure', labelPositionX, labelPositionY + labelPositionYOffSetForInsight)
							.css({
								fontSize: labelSize + 'px',
								color: '#000',
								fontFamily: '"FortBook", Arial, sans-serif'
							})
							.add();
						ren.text(
							this.customAttributes ? this.customAttributes.higherLabel : 'Higher Exposure',
							this.configuration.chart.width * 0.5 + labelPositionX,
							labelPositionY + labelPositionYOffSetForInsight
						)
							.css({
								fontSize: labelSize + 'px',
								color: '#000',
								fontFamily: '"FortBook", Arial, sans-serif'
							})
							.add();
						if (get(this.customAttributes, 'benchmarkFactorValue')) {
							(<number[]>this.customAttributes.benchmarkFactorValue).forEach((factorValue, index) => {
								if (factorValue) {
									const tickRange = tickPositions[2] - tickPositions[0];
									ren.rect(
										((this.configuration.chart.width * factorValue) / tickRange) - 3,
										marginTop + marginTopOffSetForInsight + spacingTop + index * (spacingBetweenGroup),
										3,
										indexLineHeight
									)
										.attr({ fill: ColorsConfig.CTA_COLOR })
										.add()
										.toFront();
								}
							});
						}
						if (chartType === 'insight') {
							this.seriesCategories.forEach((category, index) => {
								if (category) {
									ren.text(
										category,
										labelSize < 10 ? 5 : 10,
										marginTop +
										marginTopOffSetForInsight +
										spacingTop +
										index * (spacingBetweenGroup) -
										(labelSize < 10 ? 2 : 10)
									)
										.css({
											fontSize: labelSize + 'px',
											color: '#000',
											fontFamily: '"FortBook", Arial, sans-serif'
										})
										.add();
								}
							});
						}
					}
				}
			},
			plotOptions: {
				scatter: {
					marker: {
						symbol: ChartType.Line,
						lineWidth: 3,
						radius: 9,
						lineColor: ColorsConfig.CTA_COLOR
					}
				}
			},
			colors: this.seriesColors,
			legend: this.name === ('center-align-chart') ? { y: -this.height - 1 + marginTop }
				: this.customAttributes.legend || { enabled: false },
			series: this.seriesData,
			xAxis: {
				categories: this.seriesCategories,
				plotLines: [
					{
						width: 0
					}
				],
				lineColor: '#a5a5a5'
			},
			yAxis: {
				gridLineWidth: this.customAttributes?.autoTickPositions || this.customAttributes?.shouldShowGridLine ? 1 : 0,
				labels: this.customAttributes?.autoTickPositions || this.customAttributes?.shouldFormatValue ? {
					enabled: true,
					style: {
						fontSize: labelSize + 'px'
					},
					formatter: function (): string {
						return this.value + '%';
					}
				} : {
					enabled: true,
					style: {
						fontSize: labelSize + 'px'
					}
				},
				tickPositions: tickPositions,
				plotLines: this.customAttributes?.autoTickPositions ? [
					{
						color: '#a5a5a5',
						width: 1,
						value: tickPositions[0]
					},
					{
						color: '#a5a5a5',
						width: 1,
						value: 0,
						dashStyle: 'solid',
						zIndex: 9
					},
					{
						color: '#a5a5a5',
						width: 1,
						value: tickPositions[tickPositions.length - 1]
					}
				] : [
					{
						color: '#a5a5a5',
						width: 1,
						value: 0,
						dashStyle: 'dashed',
						zIndex: 9
					},
					{
						color: '#a5a5a5',
						width: 1,
						value: tickPositions[tickPositions.length - 1]
					}
				],
				title: {
					enabled: true,
					text: this.customAttributes ? this.customAttributes.chartLabel : 'Factor Box Score',
					style: {
						fontSize: labelSize + 'px',
						color: '#000'
					}
				},
				min: 0,
				max: 100
			}
		});

		if (chartType === 'insight') {
			// customization for insight chart
			this.configuration = ObjectUtil.extend(this.configuration, {
				plotOptions: {
					series: {
						pointPadding: 0.15,
						groupPadding: 0.3
					}
				}
			});
		}

		if (this.customAttributes?.auxiliaryData) {
			this.configuration.series = ChartFactory.enrichSeriesWithAuxiliaryData(this.seriesData, this.customAttributes.auxiliaryData);
		}

		if (this.customAttributes) {
			this.configuration = ObjectUtil.extend(this.configuration, this.customAttributes);
		}

		if (this.name === 'center-align-chart') {
			this.configuration.yAxis.plotLines = merge(this.configuration.yAxis.plotLines, [{ dashStyle: 'Dash' }, {}, { dashStyle: 'Dash' }]);
			this.configuration = ObjectUtil.extend(this.configuration, BENCHMARK_CHART_CONFIG);
		}
	}

	public getConfigurationForPDF(): any {
		if (this.name === 'center-align-chart') {
			return this.configuration = ObjectUtil.extend(this.configuration, {
				chart: {
					marginTop: -2
				},
				legend: {
					enabled: false
				},
				xAxis: {
					plotLines: [{
						width: 0
					}]
				}
			});
		} else {
			return ObjectUtil.extend(this.configuration, {
				chart: {
					marginTop: -3,
					marginBottom: 1
				},
				legend: {
					enabled: false
				},
				xAxis: {
					visible: false
				},
				yAxis: {
					visible: false
				}
			});
		}
	}

	public render(): void {
		Highcharts.chart(this.configuration);
	}
}

export interface BarChartCustomAttributes {
	// template interface for now in case someone need manual config on these chart
	[id: string]: any;
}
